// src/components/Colaborador/HistoricoSalarial.js
import React, { useState, useMemo, useEffect } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ITENS_POR_PAGINA = 12;

const HistoricoSalarial = ({ historico }) => {
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear().toString());
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [salarioVisivel, setSalarioVisivel] = useState(false);

  const { anosDisponiveis, historicoProcessado } = useMemo(() => {
    if (!Array.isArray(historico) || historico.length === 0) {
      return { anosDisponiveis: [], historicoProcessado: [] };
    }

    const anos = new Set();
    const processado = historico
      .map((item, index) => {
        let dataObj = null;
        let ano = null;
        let mesAno = 'Inválido';
        if (item.data && typeof item.data.seconds === 'number') {
          dataObj = new Date(item.data.seconds * 1000);
        } else if (item.data instanceof Date) {
          dataObj = item.data;
        }

        if (dataObj instanceof Date && !isNaN(dataObj)) {
          ano = dataObj.getFullYear();
          mesAno = `${String(dataObj.getMonth() + 1).padStart(2, '0')}/${ano}`;
          anos.add(ano.toString());
        }

        return {
          ...item,
          internalKey: item.id || `${item.data?.seconds}-${index}` || `${Date.now()}-${index}`,
          dataObj: dataObj,
          ano: ano ? ano.toString() : null,
          mesAno: mesAno,
          salarioFormatado: Number(item.salario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        };
      })
      .sort((a, b) => (b.dataObj?.getTime() || 0) - (a.dataObj?.getTime() || 0));

    const anosOrdenados = [...anos].sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
    return {
        anosDisponiveis: anosOrdenados,
        historicoProcessado: processado
    };
  }, [historico]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [anoSelecionado]);

  const historicoFiltradoPorAno = useMemo(() => {
    if (anoSelecionado === 'todos') {
        return historicoProcessado;
    }
    return historicoProcessado.filter(item => item.ano === anoSelecionado);
  }, [historicoProcessado, anoSelecionado]);

  const historicoPaginado = useMemo(() => {
    const indiceInicial = (paginaAtual - 1) * ITENS_POR_PAGINA;
    const indiceFinal = indiceInicial + ITENS_POR_PAGINA;
    return historicoFiltradoPorAno.slice(indiceInicial, indiceFinal);
  }, [historicoFiltradoPorAno, paginaAtual]);

  useEffect(() => {
    const anoAtualStr = new Date().getFullYear().toString();
    const temDadosNoAnoAtual = historicoProcessado.some(item => item.ano === anoAtualStr);

    if (anosDisponiveis.length > 0 && !temDadosNoAnoAtual && anoSelecionado === anoAtualStr && anosDisponiveis[0] !== anoAtualStr) {
      setAnoSelecionado(anosDisponiveis[0]);
    } else if (anosDisponiveis.length > 0 && !anosDisponiveis.includes(anoSelecionado) && anoSelecionado !== 'todos') {
      setAnoSelecionado(anosDisponiveis[0]);
    } else if (anosDisponiveis.length === 0 && anoSelecionado !== 'todos') {
       setAnoSelecionado(new Date().getFullYear().toString());
    }
  }, [anosDisponiveis, historicoProcessado, anoSelecionado]);


  if (historicoProcessado.length === 0) {
    return <p className="text-muted">Nenhum histórico de recebimento registrado.</p>;
  }

  const totalPaginas = Math.ceil(historicoFiltradoPorAno.length / ITENS_POR_PAGINA);

  const estiloBotaoIcone = {
    background: 'transparent',
    border: 'none',
    padding: 'var(--spacer-1)',
    cursor: 'pointer',
    color: 'var(--text-color-base)', // Cor preta/cinza escuro para o ícone
  };

  return (
    <div className="historico-salarial-container">
      {/* Bloco de controles com margem inferior */}
<div className="d-flex justify-content-between align-items-end mb-3">
  {/* Agrupador para o filtro, que ficará à esquerda */}
  <div className="form-group mb-0" style={{ minWidth: '150px' }}>
    <label htmlFor="filtroAnoSalario" className="form-label">
      Filtrar por Ano:
    </label>
    <select
      id="filtroAnoSalario"
      className="form-select form-select-sm"
      value={anoSelecionado}
      onChange={(e) => setAnoSelecionado(e.target.value)}
    >
      <option value="todos">Todos os Anos</option>
      {anosDisponiveis.map(ano => (
        <option key={ano} value={ano}>{ano}</option>
      ))}
    </select>
  </div>

  {/* Botão de visibilidade, que ficará à direita */}
  <button
    type="button"
    style={estiloBotaoIcone}
    onClick={() => setSalarioVisivel(!salarioVisivel)}
    aria-pressed={salarioVisivel}
    aria-label={salarioVisivel ? "Ocultar valores salariais" : "Mostrar valores salariais"}
    title={salarioVisivel ? "Ocultar valores" : "Mostrar valores"}
  >
    {salarioVisivel ? <FaEye size={20} /> : <FaEyeSlash size={20} />}
  </button>
</div>

      {historicoPaginado.length === 0 ? (
         <p className="text-muted mt-2">
           {anoSelecionado === 'todos'
              ? "Nenhum histórico de recebimento registrado."
              : `Nenhum registro encontrado para o ano ${anoSelecionado}.`
           }
         </p>
      ) : (
        <>
          <div className="table-responsive">
              <table className="table table-striped table-hover table-sm">
                <thead>
                  <tr>
                    <th>Mês/Ano</th>
                    <th>Recebimento</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {historicoPaginado.map(item => (
                    <tr key={item.internalKey}>
                      <td>{item.mesAno}</td>
                      <td>
                        {salarioVisivel ? item.salarioFormatado : 'R$ ••••••'}
                      </td>
                      <td>{item.motivo || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </div>

          {totalPaginas > 1 && (
            <nav aria-label="Paginação do histórico de recebimento" className="mt-3 d-flex justify-content-center">
              <ul className="pagination">
                <li className={`page-item ${paginaAtual === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPaginaAtual(p => p - 1)} disabled={paginaAtual === 1}>
                    Anterior
                  </button>
                </li>
                {[...Array(totalPaginas).keys()].map(num => (
                  <li key={num + 1} className={`page-item ${paginaAtual === num + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPaginaAtual(num + 1)}>
                      {num + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${paginaAtual === totalPaginas ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPaginaAtual(p => p + 1)} disabled={paginaAtual === totalPaginas}>
                    Próximo
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
       )}
    </div>
  );
};

export default HistoricoSalarial;